// src/components/proposals/ProposalsTab.jsx
//
// The MSP OS chrome around the Bronze template: dashboard (list) + builder
// (settings pane | live preview) + the actions (create / duplicate / archive /
// snapshot / export PDF).
//
// Lives outside MspOsApp.jsx for the same reason MonthlyReports.jsx does — it's
// a large self-contained feature, and MspOsApp is already 15k lines. MspOsApp
// owns the `proposals` state and the blob save; this component receives them.
//
// Proposals persist through the existing state blob (PUT /api/state), exactly
// like quotes. The only backend call here is POST /api/proposals/render-pdf.
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { renderToString } from "react-dom/server";
import { getProposalPrintCss } from "./BronzeTemplate";
import { templateComponentFor } from "./templateRegistry";
import { Panel, SettingsPanel } from "./ProposalSettingsPanel";
import { proposalCompanyPatch } from "../common/companyLink";
import LeadSettingsPanel from "./LeadSettingsPanel";
import ContractsPanel from "../contracts/ContractsPanel";
import ProposalKanban from "./ProposalKanban";
import { STATUSES, STATUS_STYLE } from "./proposalStatus";
import { webViewBadgesOf, dashboardBadgesOf, needsWonReconciliation,
         approvalStatusFor, hasApprovalSend } from "./webViewBadges";
import { DEFAULT_PROPOSAL_TEMPLATES, mergeTemplates, makeProposalRecord,
         docTypeOf, isLead, DOC_LEAD, DOC_PROPOSAL } from "./templateDefaults";
import { backfillProposalNumbers, formatProposalNumber, matchesNumberSearch } from "./proposalNumber";
import { backfillCategories } from "./lineItemCategory";
import { reserveNumber, ensureAtLeast } from "../../api/sequenceApi";

const API_BASE = window.location.hostname === "localhost"
  ? "http://localhost:3001"
  : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

/* The enum and its palette live in proposalStatus.js — a module with no imports
   — so tests can reason about status without loading ProposalsTab, which pulls
   MSAL in behind it. Re-exported here because this file was their home. */
export { STATUSES, STATUS_STYLE } from "./proposalStatus";

const TIER_COLOR = {
  "Copper Defender": "#b45309",
  "Bronze Sentinel": "#92400e",
  "Silver Guardian": "#64748b",
  "Gold Fortress":   "#0b80a5",
};

/* Leads share the dashboard with proposals, so they need a colour of their own
   that is NOT one of the four tier colours — a Lead covers all four tiers, and
   borrowing one would read as "this is a Bronze document". Violet is unused
   elsewhere on this board. */
const LEAD_ACCENT = "#7c3aed";

/* What a continuation page belongs to, for the pre-flight overflow warning.
   A first page is named by its own <h1>/<h2>; a continuation carries no
   heading of its own, so it is identified by the flow it is part of. */
const FLOW_LABELS = {
  foundational:  "Foundational Upgrade Plan (continued)",
  investment:    "Your IT Investment (continued)",
  "lead-matrix": "What's Included (continued)",
  "lead-addons": "Optional Add-Ons (continued)",
  "lead-terms":  "Terms & Conditions (continued)",
};

const newId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const fmtDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

/* Cover images are stored once per proposal in `proposal.coverImages`, keyed by
   id; `content.coverImageId` references one. Snapshots deep-clone content, so
   this keeps a snapshot at a few KB instead of carrying its own copy of a
   ~300-500KB base64 image into a state blob that also holds quotes and freight.

   Immutability still holds: changing the cover mints a NEW id, so an existing
   snapshot keeps resolving the image it was taken with. */
function hydrateContent(proposal, content) {
  if (!content) return content;
  const id = content.coverImageId;
  return { ...content, coverImage: (id && proposal?.coverImages?.[id]) || null };
}

/* Drop any stored image no longer referenced by the live content or any
   snapshot — otherwise re-picking the cover a few times leaks copies. */
function pruneCoverImages(proposal) {
  const images = proposal.coverImages || {};
  const used = new Set();
  if (proposal.content?.coverImageId) used.add(proposal.content.coverImageId);
  (proposal.snapshots || []).forEach((s) => {
    if (s.content?.coverImageId) used.add(s.content.coverImageId);
  });
  const kept = {};
  Object.keys(images).forEach((k) => { if (used.has(k)) kept[k] = images[k]; });
  return kept;
}

/* Escape for the <title> of the generated print document. */
function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/* Assemble the full HTML document DocRaptor renders.
   Option 1 from the brief: renderToString here rather than duplicating the
   template server-side. `assetBase` must be absolute — DocRaptor fetches the
   logos from the SWA, it has no page context to resolve a relative path. */
export function renderTemplateAsHtmlString(templateKey, content) {
  const Template = templateComponentFor(templateKey);
  const body = renderToString(
    <Template content={content} mode="print" assetBase={window.location.origin} />
  );
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${escapeHtml(content?.customerName || "Proposal")}</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
<style>${getProposalPrintCss()}</style>
</head>
<body>
${body}
</body>
</html>`;
}

/* Measure the real print DOM before sending it to DocRaptor.
   `.page` is `overflow:hidden` at a fixed 297mm in print, so anything past the
   A4 box is silently CLIPPED — REFINEMENTS.md §7 (the "Once-Off Total falls
   under the footer" failure). A dense Your IT Investment page — several
   Additional Monthly Charges rows plus both add-ons ticked — is the usual
   cause. Chrome's layout is only indicative of Prince's, but it reliably
   catches the gross overflow case.
   Resolves to an array of page labels that don't fit. */
function findOverflowingPages(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:absolute;left:-10000px;top:0;width:900px;height:1400px;border:0;visibility:hidden;";
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      try { document.body.removeChild(iframe); } catch (e) { /* already gone */ }
      resolve(result);
    };
    iframe.onload = () => {
      // Give webfonts a moment — text metrics move page heights.
      setTimeout(() => {
        try {
          const doc = iframe.contentDocument;
          // Foundational pages are DELIBERATELY still measured. The brief asked
          // to exempt them now that the section spans several pages, but its
          // chunking is estimated rather than DOM-measured — so an oversized
          // Foundational page means the estimate under-packed, which is the
          // silent clipping this whole feature exists to remove. Exempting it
          // would hide the one signal that the estimator was wrong.
          const bad = [...doc.querySelectorAll(".page")]
            .filter((p) => p.offsetHeight > 1124)   // 1px tolerance for border rounding
            .map((p) => {
              const heading = p.querySelector("h1,h2")?.textContent?.trim();
              if (heading) return heading;
              // continuation pages carry no heading of their own
              return FLOW_LABELS[p.dataset.flow] || "Cover";
            });
          finish(bad);
        } catch (e) {
          finish([]);                                // never block the export on this
        }
      }, 400);
    };
    setTimeout(() => finish([]), 4000);             // hard timeout
    document.body.appendChild(iframe);
    iframe.srcdoc = html;
  });
}

export default function ProposalsTab({
  proposals = [],
  setProposals,
  saveSnapshot,
  quotes = [],
  setQuotes,
  userEmail,
  userName,
  proposalTemplates = [],
  setProposalTemplates,
  defaultMarkupPct = 0,
  onCatalogSearch,
  opportunities = [],
  // Offered as matches by the settings panel's company box. A proposal's
  // company is free text — this never gates anything.
  atCompanies = [],
  requestOpenId = null,
  onConsumedOpenRequest,
  contractTemplates = [],
  contractInstances = [],
  setContractInstances,
}) {
  const [openId, setOpenId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [numberSearch, setNumberSearch] = useState("");
  /* Board / List, remembered per browser exactly as quoting does
     (localStorage msp-os-quote-view). The board is DERIVED — proposalBoard.js
     reads status, contract instances and the onboarding record — and nothing on
     it drags; the list keeps the status filter and is where leads, lost and
     archived live. The number search ignores the view AND the filters, so a
     typed P number always shows the plain list of matches. */
  const [proposalView, setProposalViewState] = useState(() => {
    try { return localStorage.getItem("msp-os-proposal-view") || "board"; } catch { return "board"; }
  });
  const setProposalView = (v) => { setProposalViewState(v); try { localStorage.setItem("msp-os-proposal-view", v); } catch { /* private mode */ } };
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewSnapshot, setViewSnapshot] = useState(null); // version number or null
  // { id, kind } — the same modal serves the client send and the internal
  // approval request; only the copy and the default recipient differ.
  const [sendModal, setSendModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // proposal id pending deletion
  // docType being created, or null. Shown only when >1 template of that type is
  // active — Leads have exactly one, so + New Lead never opens it.
  const [pickTemplate, setPickTemplate] = useState(null);

  const flash = useCallback((kind, msg, ms = 3000) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), ms);
  }, []);

  const open = proposals.find((p) => p.id === openId) || null;

  const openOpportunities = useMemo(
    () => opportunities.filter((o) => (o.pipelineStatus || "open") === "open"),
    [opportunities],
  );

  /* Arriving from an Opportunity ("Create Proposal", or clicking a linked one).
     The request is cleared once consumed so returning to the tab later doesn't
     silently reopen the same proposal. */
  useEffect(() => {
    if (!requestOpenId) return;
    setOpenId(requestOpenId);
    setViewSnapshot(null);
    onConsumedOpenRequest?.();
  }, [requestOpenId, onConsumedOpenRequest]);

  /* ── Web-view activity ───────────────────────────────────────────────────
     Opens, questions and acceptance for the OPEN proposal only. They live in a
     per-proposal blob rather than the state blob (a public write and the SPA's
     debounced save would otherwise clobber each other), so they cost a request
     — which is why this loads one proposal at a time and the dashboard shows
     only what is already in memory. */
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  /* RETURNS the payload rather than setting state itself, so the caller decides
     whether the answer is still wanted. A fetch that set state directly would
     let a slow response for proposal A paint its opens onto proposal B — the
     panel would look right, against the wrong client. */
  const fetchActivity = useCallback(async (id) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(id)}/view-activity`,
        { headers: { Authorization: `Bearer ${window.__mspToken || ""}` } },
      );
      if (!res.ok) throw new Error(res.statusText);
      return await res.json();
    } catch (err) {
      // A failed load must not break the builder. The proposal itself comes
      // from state and is unaffected; only this panel goes quiet.
      return { published: false, error: err.message };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    if (!openId) return undefined;
    setActivityLoading(true);
    fetchActivity(openId).then((data) => {
      if (cancelled) return;              // a different proposal is open now
      setActivity(data);
      setActivityLoading(false);
    });
    return () => { cancelled = true; };
  }, [openId, fetchActivity]);

  /* The panel's ↻ button. Always applies, because it can only be pressed on the
     proposal that is currently open. */
  const refreshActivity = useCallback(async (id) => {
    setActivityLoading(true);
    const data = await fetchActivity(id);
    setActivity(data);
    setActivityLoading(false);
  }, [fetchActivity]);

  /* ── Actions ─────────────────────────────────────────────────────────── */

  /* Takes an updater, not a value. Two commits can fire from one handler — the
     company picker patches the proposal and then the content — and a
     value-based commit would have both map over the same stale `proposals`
     closure, so the second silently discards the first. */
  const commit = useCallback((updater) => {
    setProposals((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveSnapshot({ proposals: next });
      return next;
    });
  }, [setProposals, saveSnapshot]);

  const activeTemplatesOfType = (docType) =>
    proposalTemplates.filter((t) => t.status === "active" && docTypeOf(t) === docType);

  /* Seeds from a template's defaults rather than the component's hardcoded
     ones, so tuning a template changes what the next document starts with.
     The clone in seedContentFromTemplate is what keeps that one-directional —
     later template edits can never reach into an existing record. */
  /* ── Backfill: give every existing proposal a P number ──────────────────
     Runs on load and is IDEMPOTENT, which is the whole design: a record that
     already has a number keeps it, so this settles after one pass and does
     nothing on every load after that. Same self-healing shape as
     ensureActiveTemplates below.

     Two halves, and the ORDER MATTERS. The records are numbered first, then
     the server counter is carried past them — do it the other way round and a
     crash in between leaves the counter advanced over numbers no record holds,
     which burns them harmlessly; do it in this order and the same crash leaves
     the counter behind, so the next new proposal would collide with a
     backfilled one. Advancing the counter is therefore the LAST thing. */
  const backfilledRef = React.useRef(false);
  useEffect(() => {
    if (backfilledRef.current) return;
    if (!proposals.length) return;                     // nothing loaded yet
    const { proposals: next, assigned, nextFree } = backfillProposalNumbers(proposals);
    if (!assigned) { backfilledRef.current = true; return; }

    backfilledRef.current = true;
    commit(() => next);
    ensureAtLeast("proposal", nextFree).catch((e) => {
      /* The records are numbered either way. A failure here means the counter
         may still be behind, so say so loudly rather than let the next new
         proposal quietly collide with a backfilled one. */
      // eslint-disable-next-line no-console
      console.error("Proposal numbers were assigned but the counter was not advanced.", e);
      flash("err", "Numbers assigned, but the counter did not advance — reload before creating a new proposal.");
    });
    flash("ok", `Numbered ${assigned} proposal${assigned === 1 ? "" : "s"}.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals.length]);

  /* ── Backfill: suggest a category for every foundational line item ──────
     Runs on load and is idempotent, the same shape as the P-number backfill
     above and ensureActiveTemplates below.

     ⚠ WHAT THIS WRITES IS A SUGGESTION, NOT AN ANSWER. Every item it touches
     is stored with categoryConfirmed:false and shows an amber "suggested,
     please check" in the editor until somebody opens the dropdown. The
     category decides a block's LABOUR total, which decides its Fixed Price
     contract's hours — so a guess left unreviewed would quietly become a
     billed figure. The suggestion exists to save typing over ~40 existing
     items, not to answer the question. */
  const categoriesRef = React.useRef(false);
  useEffect(() => {
    if (categoriesRef.current) return;
    if (!proposals.length) return;

    let touched = 0;
    const next = proposals.map((p) => {
      const blocks = p.content?.foundationalBlocks;
      if (!Array.isArray(blocks) || !blocks.length) return p;
      const out = backfillCategories(blocks);
      if (!out.suggested) return p;
      touched += out.suggested;
      return { ...p, content: { ...p.content, foundationalBlocks: out.blocks } };
    });

    categoriesRef.current = true;
    if (!touched) return;
    commit(() => next);
    flash("ok", `Suggested a category for ${touched} line item${touched === 1 ? "" : "s"} — please review.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals.length]);

  /* Self-heal rather than dead-end. Without this, "+ New Proposal" (or
     "+ New Lead") opens a picker listing nothing, with only a Cancel button:
     a silent, unrecoverable dead end on the feature's primary action. */
  const ensureActiveTemplates = (docType) => {
    const hasType = (list) =>
      list.some((t) => t.status === "active" && docTypeOf(t) === docType);
    if (hasType(proposalTemplates)) return proposalTemplates;

    /* A template that shipped AFTER this blob was written is ABSENT, not
       inactive — which is exactly the Lead's situation for every existing
       user, since the seed only ever runs on an empty array. mergeTemplates
       adds it without overwriting anything stored. Same trap that left Gold
       greyed out on a green deploy. */
    const merged = mergeTemplates(proposalTemplates);
    if (hasType(merged)) {
      setProposalTemplates(merged);
      saveSnapshot({ proposalTemplates: merged });
      return merged;
    }

    // Nothing usable at all — a partial save, or everything flipped to
    // coming-soon. A one-off reset to defaults beats a dead button.
    const seeded = DEFAULT_PROPOSAL_TEMPLATES();
    setProposalTemplates(seeded);
    saveSnapshot({ proposalTemplates: seeded });
    flash("info", "Proposal templates were missing — restored the defaults");
    return seeded;
  };

  /* One creation path for both document types. `docType` scopes which templates
     are offered — "+ New Proposal" must never open a picker that lists the Lead
     alongside Bronze and Gold, and vice versa. */
  const createDocument = async (docType, templateKey) => {
    const templates = ensureActiveTemplates(docType);
    const active = templates.filter((t) => t.status === "active" && docTypeOf(t) === docType);
    if (!active.length) {
      flash("err", docType === DOC_LEAD
        ? "The Lead template isn't available — open Proposal Templates to check it"
        : "No proposal template is active");
      return;
    }

    const key = templateKey || (active.length === 1 ? active[0].key : null);
    if (!key) { setPickTemplate(docType); return; }

    const template = active.find((t) => t.key === key);
    if (!template) {
      flash("err", "That template isn't available yet");
      return;
    }

    // Shared factory — the Opportunity entry point creates proposals too, and
    // two independent literals would drift. Async because it reserves the P
    // number from the server counter.
    const p = await makeProposalRecord(template, { salesRepName: userName });
    commit((prev) => [...prev, p]);
    setPickTemplate(null);
    setOpenId(p.id);
    setViewSnapshot(null);
  };

  const createProposal = (templateKey) => createDocument(DOC_PROPOSAL, templateKey);
  const createLead     = () => createDocument(DOC_LEAD);

  const patchProposal = (id, patch) =>
    commit((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch, updatedAt: Date.now() } : p)));

  const updateContent = (id, patch) =>
    commit((prev) => prev.map((p) =>
      p.id === id ? { ...p, content: { ...p.content, ...patch }, updatedAt: Date.now() } : p));

  /* The company box writes to BOTH halves of the record: the display name lives
     in content.customerName, the optional Autotask link at the top level.
     See companyLink.js for why they are stored apart.

     ⚠ ONE commit, not updateContent() followed by patchProposal(). Both of those
     take the updater form so a pair of them WOULD compose correctly today — but
     this precise handler is the one CLAUDE.md's "paired state writes" note was
     written about: the old company picker patched the record and then the
     content, both mapping over the same stale closure, and the second silently
     discarded the first. companyName persisted as null while the UI looked
     perfectly correct. A single write cannot regress that way no matter how the
     helpers are later refactored, and it saves the blob once instead of twice. */
  const setProposalCompany = (id, name, match) => {
    const { content, patch } = proposalCompanyPatch(name, match);
    commit((prev) => prev.map((p) => (p.id === id
      ? { ...p, ...patch, content: { ...p.content, ...content }, updatedAt: Date.now() }
      : p)));
  };

  /* Store a newly-uploaded cover under a fresh id, point content at it, and
     drop any image no longer referenced. A fresh id per upload is what keeps
     existing snapshots pinned to the image they were taken with. */
  const setCoverImage = (id, dataUrl) =>
    commit((prev) => prev.map((p) => {
      if (p.id !== id) return p;
      let next;
      if (!dataUrl) {
        next = { ...p, content: { ...p.content, coverImageId: null }, updatedAt: Date.now() };
      } else {
        const imgId = newId("img");
        next = {
          ...p,
          coverImages: { ...(p.coverImages || {}), [imgId]: dataUrl },
          content: { ...p.content, coverImageId: imgId },
          updatedAt: Date.now(),
        };
      }
      return { ...next, coverImages: pruneCoverImages(next) };
    }));

  const duplicateProposal = async (id) => {
    const src = proposals.find((p) => p.id === id);
    if (!src) return;

    /* ⚠ A COPY IS A DIFFERENT DOCUMENT AND TAKES ITS OWN NUMBER. `...src`
       below carries every field, so without this the copy would inherit the
       source's P number and two proposals would claim one identity — the exact
       defect the atomic counter exists to prevent, arriving through the spread
       instead of through a race. A Lead takes none, as ever. */
    let copyNumber = null;
    if (!isLead(src)) {
      try { copyNumber = (await reserveNumber("proposal")).number; }
      catch { flash("err", "Could not reserve a number for the copy — it will be filled in on reload."); }
    }

    const copy = {
      ...src,
      proposalNumber: copyNumber,
      id: newId("prop"),
      title: `${src.title} (Copy)`,
      status: "draft",
      content: JSON.parse(JSON.stringify(src.content)),
      // Carry only the image the copy actually references — the source's older
      // covers belong to its snapshots, and the copy has none.
      coverImages: src.content?.coverImageId && src.coverImages?.[src.content.coverImageId]
        ? { [src.content.coverImageId]: src.coverImages[src.content.coverImageId] }
        : {},
      snapshots: [],                    // a copy starts with no version history
      quoteId: null,
      // opportunityId is deliberately NOT cleared (it rides in via the spread):
      // duplicating is how a revised proposal for the same deal gets made, so
      // the copy belongs to the same opportunity and should count toward it.
      createdAt: Date.now(),
      updatedAt: Date.now(),
      publicToken: null, publicUrl: null, clickTracking: [],
    };
    commit((prev) => [...prev, copy]);
    flash("ok", "Proposal duplicated");
  };

  const setStatus = (id, status) => patchProposal(id, { status });

  /* ── Acceptance reconciliation ───────────────────────────────────────────
     A client's Accept is recorded in the view blob by a public route, which
     deliberately cannot write to the state blob. The SPA is the only writer of
     `status`, so it closes the loop here.

     Declared AFTER patchProposal on purpose: CLAUDE.md's "helpers referenced
     before declared" trap produces a minifier-only "Cannot access before
     initialization" that never shows up in development.

     Stamps acceptanceReconciledAt so this fires exactly once. Without the
     stamp, a proposal the client accepted and Craig then moved to "lost" would
     be dragged back to "won" on every activity load — the app arguing with the
     person using it.

     Deliberately does NOT touch opportunity.pipelineStatus. Closing the deal
     stays a manual call: colour is derived, and reaching green is not the same
     as closing. */
  useEffect(() => {
    if (!openId || !open) return;
    if (!needsWonReconciliation(open, activity)) return;
    patchProposal(openId, { status: "won", acceptanceReconciledAt: Date.now() });
    flash("ok", `${open.content?.customerName || "The client"} accepted — marked won`, 7000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, activity]);

  /* The approver's decision, reconciled the same way — recorded publicly, and
     the SPA is the only writer of `status`. Stamped with the DECISION's
     timestamp rather than a boolean, so a second round (changes_needed, then
     approved) reconciles again instead of being ignored as already-done. */
  useEffect(() => {
    if (!openId || !open) return;
    const next = approvalStatusFor(open, activity);
    if (!next) return;
    patchProposal(openId, { status: next, approvalReconciledAt: activity.approval.decidedAt });
    flash(next === "approved" ? "ok" : "err",
      next === "approved"
        ? `Authorised by ${activity.approval.decidedByName || "the approver"}`
        : `Changes requested by ${activity.approval.decidedByName || "the approver"}`, 8000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, activity]);

  /* PERMANENT delete — distinct from Archive, which stays soft and recoverable.
     Deliberately routed through commit() (setProposals + saveSnapshot) rather
     than a direct PUT /api/state. Deleting server-side leaves this component's
     React state holding the object, and the next save writes it straight back —
     deleted data silently resurrecting. Going through state mutates and
     persists in one step, so there is no window for the two to diverge. */
  const deleteProposal = (id) => {
    const p = proposals.find((x) => x.id === id);
    commit((prev) => prev.filter((x) => x.id !== id));
    if (openId === id) { setOpenId(null); setViewSnapshot(null); }
    setConfirmDelete(null);
    flash("ok", `Deleted "${p?.companyName || p?.content?.customerName || "proposal"}"`);
  };

  const snapshotProposal = (id, reason = "sent") => {
    const p = proposals.find((x) => x.id === id);
    if (!p) return;
    const version = (p.snapshots?.length || 0) + 1;
    const snap = {
      id: newId("snap"),
      version,
      snappedAt: Date.now(),
      snappedBy: userEmail || "unknown",
      content: JSON.parse(JSON.stringify(p.content)),
      status: reason,
      pdfBlobUrl: null,
    };
    commit((prev) => prev.map((x) => x.id !== id ? x : {
      ...x,
      snapshots: [...(x.snapshots || []), snap],
      status: reason === "sent" ? "sent" : x.status,
      updatedAt: Date.now(),
    }));
    flash("ok", `Snapshot saved as v${version}`);
  };

  const exportPdf = async (id, snapshotVersion = null, { test = false } = {}) => {
    const p = proposals.find((x) => x.id === id);
    if (!p) return;
    const stored = snapshotVersion
      ? p.snapshots?.find((s) => s.version === snapshotVersion)?.content
      : p.content;
    if (!stored) { flash("err", "That snapshot no longer exists"); return; }
    const content = hydrateContent(p, stored);   // resolve coverImageId -> base64

    const safeName = (p.companyName || p.content?.customerName || "proposal")
      .replace(/[^a-zA-Z0-9]/g, "_");
    const filename = `${safeName}-${p.templateKey}-v${snapshotVersion || "draft"}.pdf`;

    setBusy(true);
    setToast({ kind: "info", msg: test ? "Generating test PDF…" : "Generating PDF…" });
    try {
      const html = renderTemplateAsHtmlString(p.templateKey, content);

      // Pre-flight: anything taller than A4 is clipped, not reflowed.
      const overflowing = await findOverflowingPages(html);
      if (overflowing.length) {
        const ok = window.confirm(
          `These pages are taller than A4 and will be CUT OFF in the PDF:\n\n` +
          overflowing.map((n) => `  • ${n}`).join("\n") +
          `\n\nUsually caused by too many "Additional Monthly Charges" rows, or ` +
          `long challenge/finding text. Remove a row or shorten the text to fix it.\n\n` +
          `Generate the PDF anyway?`
        );
        if (!ok) { setBusy(false); setToast(null); return; }
      }

      const res = await fetch(`${API_BASE}/api/proposals/render-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__mspToken || ""}`,
        },
        // test:true => free, watermarked. Omitting it entirely would let the
        // backend default to NODE_ENV, which bills a real document in prod.
        body: JSON.stringify({ html, filename, test }),
      });
      if (!res.ok) {
        // The route returns JSON on failure and binary on success.
        let detail = res.statusText;
        try { detail = (await res.json()).error || detail; } catch (e) { /* binary or empty */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      flash("ok", test ? "Test PDF downloaded (watermarked, not charged)" : "PDF downloaded");
    } catch (err) {
      flash("err", `PDF export failed: ${err.message}`, 6000);
    } finally {
      setBusy(false);
    }
  };

  /* ── Send to client (public web view) ────────────────────────────────────
     Publishes the proposal to its own public URL and emails the link.

     The HTML is rendered HERE, not on the server: these templates are React and
     re-implementing them server-side would be a fifth place every template
     change has to land. What the backend stores is the finished string, frozen
     — so editing the draft afterwards cannot change what a customer is
     currently reading. */
  const sendProposalLink = async (id, { recipients, emailBodyText, kind = "client" }) => {
    const p = proposals.find((x) => x.id === id);
    if (!p) return;

    setBusy(true);
    setToast({ kind: "info", msg: `Publishing and sending to ${recipients.length}…` });
    try {
      /* Snapshot FIRST, through the existing helper. It deep-clones content into
         an immutable version and flips status to "sent" — the send must publish
         exactly what was snapshotted, or the customer's document and Craig's
         record of it drift apart. */
      /* An approval request does NOT flip the proposal to "sent" — it has not
         gone to the client. It snapshots all the same, so the document the
         approver reviewed is pinned and cannot drift underneath them. */
      const version = (p.snapshots?.length || 0) + 1;
      snapshotProposal(id, kind === "internal_approval" ? "waiting_approval" : "sent");

      const content = hydrateContent(p, p.content);   // resolve coverImageId -> base64
      const html = renderTemplateAsHtmlString(p.templateKey, content);

      // Same pre-flight as Export PDF. A clipped page in a PDF Craig re-exports
      // is annoying; a clipped page in the document a customer is reading is
      // worse, and this is the last point at which it can be caught.
      const overflowing = await findOverflowingPages(html);
      if (overflowing.length) {
        const ok = window.confirm(
          `These pages are taller than A4 and will be CUT OFF:\n\n` +
          overflowing.map((n) => `  • ${n}`).join("\n") +
          `\n\nSend to the customer anyway?`
        );
        if (!ok) { setBusy(false); setToast(null); return; }
      }

      // pageCount lets the public page size its frame without measuring.
      // Every page container renders as exactly class="page" (some also carry
      // data-flow), so an exact match counts them without also catching a
      // hypothetical class="page-something".
      const pageCount = (html.match(/class="page"/g) || []).length || null;

      const res = await fetch(`${API_BASE}/api/proposals/${encodeURIComponent(id)}/send-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__mspToken || ""}`,
        },
        body: JSON.stringify({
          html, pageCount, recipients, emailBodyText, kind,
          docType: docTypeOf(p),
          customerName: p.companyName || p.content?.customerName || "",
          title: p.title || "",
        }),
      });
      if (!res.ok) {
        let detail = res.statusText;
        try { detail = (await res.json()).error || detail; } catch (e) { /* empty body */ }
        throw new Error(detail);
      }
      const out = await res.json();

      // publicToken / publicUrl hold the GENERIC link — what "Copy link" hands
      // out. Each recipient's own link lives in the view blob, not here: they
      // are per-person and there can be several, and the state blob is the one
      // place this feature deliberately keeps thin.
      /* Only the CLIENT link is kept on the record — publicUrl is what "Copy
         link" hands out, and handing out the internal review link would let a
         customer read it. Each approver's own link lives in the view blob. */
      if (kind !== "internal_approval") {
        patchProposal(id, { publicToken: out.token, publicUrl: out.publicUrl });
      }

      // Report per recipient. A batch that half-succeeded must not read as a
      // clean send — Craig needs to know WHICH address failed, not that
      // "sending failed".
      if (out.failed?.length) {
        flash("err",
          `Sent to ${out.sentTo.length}, failed for ${out.failed.map((f) => f.email).join(", ")}`,
          10000);
      } else {
        setSendModal(null);
        flash("ok",
          kind === "internal_approval"
            ? `Sent for approval to ${out.sentTo.join(", ")} — saved as v${version}`
            : `Sent to ${out.sentTo.join(", ")} — saved as v${version}`, 6000);
      }
      if (!out.pdfStored) {
        flash("err", "Sent, but the PDF copy failed to render — the Download button will be hidden.", 8000);
      }
      refreshActivity(id);
    } catch (err) {
      flash("err", `Send failed: ${err.message}`, 8000);
    } finally {
      setBusy(false);
    }
  };

  /* ── Dashboard ───────────────────────────────────────────────────────── */

  const filtered = useMemo(() => {
    const sorted = [...proposals].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    /* ⚠ THE NUMBER SEARCH IGNORES THE STATUS FILTER. Someone typing a P number
       is looking for ONE known document, and the default view hides archived
       ones — so searching for a number would silently find nothing precisely
       when the answer is "it was archived". Looking something up by its
       identifier should never depend on which view happens to be open. */
    if (String(numberSearch).trim()) {
      return sorted.filter((p) => matchesNumberSearch(p, numberSearch));
    }

    if (statusFilter === "all") return sorted;
    // "active" is the default view — everything except archived
    if (statusFilter === "active") return sorted.filter((p) => p.status !== "archived");
    return sorted.filter((p) => p.status === statusFilter);
  }, [proposals, statusFilter, numberSearch]);

  if (!open) {
    return (
      <div style={{ padding: "0 4px", width: "100%", flex: 1, minWidth: 0 }}>
        <Toast toast={toast} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 17,
                        color: "var(--text)", letterSpacing: "-0.3px" }}>Proposals</div>
          <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }} role="group" aria-label="Proposals view">
            {[["board", "Board"], ["list", "List"]].map(([id, label]) => (
              <button key={id} className={`btn ${proposalView === id ? "btn-strat" : ""}`}
                      style={{ fontSize: 10, padding: "4px 10px", borderRadius: 0, border: "none", boxShadow: "none" }}
                      aria-pressed={proposalView === id} onClick={() => setProposalView(id)}>{label}</button>
            ))}
          </div>
          {proposalView === "list" ? (
            <select className="inp" style={{ width: 150, marginBottom: 0, fontSize: 11 }}
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active</option>
              <option value="all">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_STYLE[s].label}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: 10.5, color: "var(--text3)" }} title="The board shows proposals only; the columns are derived from status, contract instances and the onboarding record.">
              Proposals only — leads, lost and archived are in the List view
            </span>
          )}
          {/* Searching by number deliberately overrides the status filter —
              see `filtered`. The hint says so, because a filter that quietly
              ignores another filter is otherwise just confusing. */}
          <input className="inp" style={{ width: 150, marginBottom: 0, fontSize: 11 }}
                 value={numberSearch}
                 onChange={(e) => setNumberSearch(e.target.value)}
                 placeholder="P number…"
                 title="Find a proposal by its number. Searches every proposal, including archived ones." />
          {numberSearch.trim() && (
            <span style={{ fontSize: 10.5, color: "var(--text3)" }}>
              {filtered.length} match{filtered.length === 1 ? "" : "es"} · all statuses
            </span>
          )}
          <div style={{ flex: 1 }} />
          {/* A Lead is a lighter, earlier document than a Proposal, so it gets
              the plain button and Proposal keeps the primary one. */}
          <button className="btn" style={{ fontSize: 11 }} onClick={createLead}
                  title="A short pre-proposal comparing all four plans — sent to find out which tier they want">
            + New Lead
          </button>
          <button className="btn btn-strat" style={{ fontSize: 11 }} onClick={() => createProposal()}>
            + New Proposal
          </button>
        </div>

        {proposalView === "board" && !numberSearch.trim() ? (
          /* The board reuses THE card — the two views must not drift. Nothing
             here drags; see ProposalKanban.jsx for why no column is a flag. */
          <ProposalKanban proposals={proposals} contractInstances={contractInstances}
                          renderCard={(p) => (
                            <ProposalCard p={p} contractInstances={contractInstances}
                                          onOpen={() => { setOpenId(p.id); setViewSnapshot(null); }}
                                          onDuplicate={() => duplicateProposal(p.id)}
                                          onArchive={() => setStatus(p.id, p.status === "archived" ? "draft" : "archived")}
                                          onDelete={() => setConfirmDelete(p.id)} />
                          )} />
        ) : filtered.length === 0 ? (
          <div style={{ padding: "60px 20px", textAlign: "center", background: "var(--s2)",
                        border: "1px solid var(--border)", borderRadius: 10 }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>
              {proposals.length === 0 ? "No proposals yet" : "Nothing matches this filter"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>
              {proposals.length === 0
                ? "Click + New Proposal to build one from a tier template, or + New Lead for a short pre-proposal comparing all four plans."
                : "Try a different status filter."}
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                        gap: 14 }}>
            {filtered.map((p) => (
              <ProposalCard key={p.id} p={p} contractInstances={contractInstances}
                            onOpen={() => { setOpenId(p.id); setViewSnapshot(null); }}
                            onDuplicate={() => duplicateProposal(p.id)}
                            onArchive={() => setStatus(p.id, p.status === "archived" ? "draft" : "archived")}
                            onDelete={() => setConfirmDelete(p.id)} />
            ))}
          </div>
        )}

        <DeleteConfirm proposal={proposals.find((p) => p.id === confirmDelete) || null}
                       onCancel={() => setConfirmDelete(null)}
                       onConfirm={deleteProposal} />

        {pickTemplate && (
          <TemplatePicker templates={activeTemplatesOfType(pickTemplate)}
                          onCancel={() => setPickTemplate(null)}
                          onPick={(k) => createDocument(pickTemplate, k)} />
        )}
      </div>
    );
  }

  /* ── Builder ─────────────────────────────────────────────────────────── */

  const PreviewTemplate = templateComponentFor(open.templateKey);
  const previewContent = hydrateContent(open, viewSnapshot
    ? open.snapshots?.find((s) => s.version === viewSnapshot)?.content || open.content
    : open.content);
  const relatedQuotes = quotes.filter((q) => q.proposalId === open.id);

  /* A Lead's content is a 4-tier matrix, not one tier's narrative, so it needs
     its own settings pane — SettingsPanel would render Bronze's pricing and
     page-visibility controls against fields a Lead does not have. */
  const openIsLead = isLead(open);
  const OpenSettingsPanel = openIsLead ? LeadSettingsPanel : SettingsPanel;
  const docNoun = openIsLead ? "lead" : "proposal";

  return (
    <div style={{ padding: "0 4px", width: "100%", flex: 1, minWidth: 0 }}>
      <Toast toast={toast} />

      {/* Action toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn" style={{ fontSize: 11 }} onClick={() => { setOpenId(null); setViewSnapshot(null); }}>
          ← All Proposals
        </button>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
          {open.companyName || open.content?.customerName || "Untitled"}
        </div>
        {openIsLead && (
          <span title="Pre-proposal — compares all four plans"
                style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 4,
                         background: LEAD_ACCENT, color: "#fff", textTransform: "uppercase",
                         letterSpacing: "0.08em" }}>Lead</span>
        )}
        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                       background: STATUS_STYLE[open.status]?.bg, color: STATUS_STYLE[open.status]?.fg,
                       textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {STATUS_STYLE[open.status]?.label}
        </span>
        {/* DERIVED, never stored, and never values in `status` — see
            webViewBadges.js. Rendered beside the status chip rather than
            replacing it, so the five-value enum and its filter stay intact. */}
        {webViewBadgesOf(open, activity, contractInstances).map((b) => (
          <span key={b.key} title={b.title || ""}
                style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                         background: b.bg, color: b.fg, textTransform: "uppercase",
                         letterSpacing: "0.06em" }}>
            {b.label}
          </span>
        ))}
        {viewSnapshot && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                         background: "#fef3c7", color: "#b45309", textTransform: "uppercase" }}>
            Viewing v{viewSnapshot} — read only
          </span>
        )}
        <div style={{ flex: 1 }} />
        <select className="inp" style={{ width: 120, marginBottom: 0, fontSize: 11 }}
                value={open.status} onChange={(e) => setStatus(open.id, e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
        </select>
        <button className="btn" style={{ fontSize: 11 }} disabled={busy}
                title="Free watermarked render — use while drafting"
                onClick={() => exportPdf(open.id, viewSnapshot, { test: true })}>
          🧪 Test PDF
        </button>
        <button className="btn" style={{ fontSize: 11 }} disabled={busy}
                title="Final unwatermarked render — consumes a paid DocRaptor document"
                onClick={() => exportPdf(open.id, viewSnapshot, { test: false })}>
          {busy ? "Generating…" : `⬇ Export PDF${viewSnapshot ? ` (v${viewSnapshot})` : ""}`}
        </button>
        <button className="btn btn-strat" style={{ fontSize: 11 }}
                onClick={() => snapshotProposal(open.id, "sent")}>
          📌 Save as sent
        </button>
        {/* Publishes to a public URL and emails it. Disabled while viewing a
            snapshot: what gets sent is always the current document, and sending
            from a read-only historical view would be ambiguous about which. */}
        {/* ADVISORY ONLY — this never gates the client send beside it. */}
        <button className="btn" style={{ fontSize: 11 }}
                disabled={busy || !!viewSnapshot}
                title="Send an internal review copy — does not go to the client"
                onClick={() => setSendModal({ id: open.id, kind: "internal_approval" })}>
          {hasApprovalSend(activity) ? "🛡 Request approval again" : "🛡 Send for approval"}
        </button>
        <button className="btn btn-strat" style={{ fontSize: 11 }}
                disabled={busy || !!viewSnapshot}
                title={viewSnapshot
                  ? "Exit the snapshot view to send the current version"
                  : `Publish to a customer link and email it`}
                onClick={() => setSendModal({ id: open.id, kind: "client" })}>
          {open.publicUrl ? "📤 Resend link" : "📤 Send to client"}
        </button>
        {open.publicUrl && (
          <button className="btn" style={{ fontSize: 11 }}
                  title={open.publicUrl}
                  onClick={() => {
                    navigator.clipboard?.writeText(open.publicUrl);
                    flash("ok", "Customer link copied");
                  }}>
            🔗 Copy link
          </button>
        )}
        <button className="btn btn-danger" style={{ fontSize: 11 }}
                title="Permanently delete this proposal and all its version history"
                onClick={() => setConfirmDelete(open.id)}>
          🗑 Delete
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 35%) 1fr", gap: 14,
                    alignItems: "start" }}>
        {/* Left — settings */}
        {/* Own scroll container. The app shell is overflow:hidden all the way up,
            so a column taller than the viewport is simply unreachable — the
            settings pane runs ~1900px against an ~870px viewport once the list
            editors are open. Mirrors the preview pane's treatment. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12,
                      maxHeight: "calc(100vh - 190px)", overflowY: "auto", paddingRight: 4 }}>
          <OpenSettingsPanel
            p={open}
            onContent={(patch) => updateContent(open.id, patch)}
            markupPct={defaultMarkupPct}
            onCatalogSearch={onCatalogSearch}
            coverImageUrl={previewContent.coverImage}
            onCover={(dataUrl) => setCoverImage(open.id, dataUrl)}
            disabled={!!viewSnapshot}
            openOpportunities={openOpportunities}
            linkedOpportunity={opportunities.find((o) => o.id === open.opportunityId) || null}
            onLinkOpportunity={(oppId) => patchProposal(open.id, { opportunityId: oppId })}
            atCompanies={atCompanies}
            onCompany={(name, match) => setProposalCompany(open.id, name, match)}
          />

          {/* Contracts — WON proposals only, and never on a Lead. A
              pre-proposal is not something anyone signs a contract against.
              Marking a proposal won does not send anything; sending is always
              an explicit action inside this panel. Hidden while viewing a
              snapshot, since a historical version is not a thing to send from. */}
          {open.status === "won" && !openIsLead && !viewSnapshot && setContractInstances && (
            <Panel title="Contracts">
              <ContractsPanel
                proposal={open}
                contractTemplates={contractTemplates}
                contractInstances={contractInstances}
                setContractInstances={setContractInstances}
                patchProposal={(patch) =>
                  commit((prev) => prev.map((p) => (p.id === open.id ? { ...p, ...patch } : p)))}
                saveSnapshot={saveSnapshot}
                flash={(msg, kind) => flash(kind === "err" ? "err" : "ok", msg)}
                quotes={quotes}
                setQuotes={setQuotes}
              />
            </Panel>
          )}

          <ActivityPanel activity={activity} loading={activityLoading}
                         proposal={open} docNoun={docNoun}
                         onRefresh={() => refreshActivity(open.id)} />

          <Panel title="Version history">
            {(open.snapshots || []).length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--text3)" }}>
                No snapshots yet. "Save as sent" freezes the current content as v1.
              </div>
            ) : (
              <>
                <div className="prop-row" style={{ marginBottom: 6 }}>
                  <button className="btn" style={{ fontSize: 10, padding: "3px 9px" }}
                          onClick={() => setViewSnapshot(null)}
                          disabled={!viewSnapshot}>Back to current draft</button>
                </div>
                {[...open.snapshots].reverse().map((s) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8,
                                           padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
                        v{s.version} · {s.status}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text3)" }}>
                        {fmtDate(s.snappedAt)} · {s.snappedBy}
                      </div>
                    </div>
                    <button className="btn" style={{ fontSize: 10, padding: "3px 8px" }}
                            onClick={() => setViewSnapshot(s.version)}>View</button>
                    <button className="btn" style={{ fontSize: 10, padding: "3px 8px" }}
                            disabled={busy} title="Free watermarked render"
                            onClick={() => exportPdf(open.id, s.version, { test: true })}>Test</button>
                    <button className="btn" style={{ fontSize: 10, padding: "3px 8px" }}
                            disabled={busy} title="Final render — consumes a paid document"
                            onClick={() => exportPdf(open.id, s.version, { test: false })}>PDF</button>
                  </div>
                ))}
              </>
            )}
          </Panel>

          <Panel title={`Quotes generated from this ${docNoun}`}>
            {relatedQuotes.length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--text3)" }}>
                None yet. In the Quoting tab, set a quote's "Related proposal" to this one.
              </div>
            ) : relatedQuotes.map((q) => (
              <div key={q.id} style={{ fontSize: 12, color: "var(--text)", padding: "4px 0",
                                       borderTop: "1px solid var(--border)" }}>
                {q.title || q.name || q.id}
                {q.status && <span style={{ color: "var(--text3)", fontSize: 10 }}> · {q.status}</span>}
              </div>
            ))}
            {setQuotes && quotes.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <label className="prop-lbl">Attach an existing quote</label>
                <select className="inp" style={{ marginBottom: 0, fontSize: 11 }} value=""
                        onChange={(e) => {
                          const qid = e.target.value;
                          if (!qid) return;
                          const next = quotes.map((q) => q.id === qid ? { ...q, proposalId: open.id } : q);
                          setQuotes(next);
                          saveSnapshot({ quotes: next });
                          flash("ok", "Quote linked to this proposal");
                        }}>
                  <option value="">Select a quote…</option>
                  {quotes.filter((q) => q.proposalId !== open.id).map((q) => (
                    <option key={q.id} value={q.id}>{q.title || q.name || q.id}</option>
                  ))}
                </select>
              </div>
            )}
          </Panel>
        </div>

        {/* Right — live preview. Scaled to fit the pane; the document itself is
            always laid out at its true 794px width so the preview matches print. */}
        <div style={{ background: "#b8b8b8", borderRadius: 10, padding: 16, overflow: "auto",
                      maxHeight: "calc(100vh - 190px)" }}>
          <div style={{ width: 794, transformOrigin: "top left", margin: "0 auto" }}>
            <PreviewTemplate
              content={previewContent}
              mode={viewSnapshot ? "view" : "edit"}
              onChange={viewSnapshot ? undefined : (patch) => updateContent(open.id, patch)}
              onCoverImage={viewSnapshot ? undefined : (dataUrl) => setCoverImage(open.id, dataUrl)}
            />
          </div>
        </div>
      </div>

      <DeleteConfirm proposal={proposals.find((p) => p.id === confirmDelete) || null}
                     onCancel={() => setConfirmDelete(null)}
                     onConfirm={deleteProposal} />

      <SendLinkModal proposal={proposals.find((p) => p.id === sendModal?.id) || null}
                     kind={sendModal?.kind || "client"}
                     activity={sendModal?.id === openId ? activity : null}
                     busy={busy}
                     senderName={userName}
                     onCancel={() => setSendModal(null)}
                     onSend={sendProposalLink} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

/* What the customer has actually done with the link.

   Reads the per-proposal view blob through GET /view-activity. Read-only by
   design: everything in here is written by public routes that deliberately
   cannot reach the state blob, and the one thing the SPA does write back —
   status, on acceptance — is handled by the reconciliation effect, not here. */
function ActivityPanel({ activity, loading, proposal, docNoun, onRefresh }) {
  if (!proposal) return null;

  const published = activity?.published;
  const views = published ? (activity.views || []) : [];
  const sends = published ? (activity.sends || []) : [];
  // Opens with no recipientId: the shared link, or a forward of a named one.
  const unattributed = views.filter((v) => !v.recipientId);
  const qa = published ? (activity.qa || []) : [];
  const acceptance = published ? activity.acceptance : null;

  const when = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    // Guard an unparseable stamp: toLocaleString returns the LITERAL string
    // "Invalid Date" rather than throwing, so a try/catch never fires — the
    // trap already documented for opportunity email timestamps.
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <Panel title="Client activity">
      {!published ? (
        <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.7 }}>
          {loading ? "Loading…" : activity?.error
            ? `Couldn't load activity: ${activity.error}`
            : `This ${docNoun} hasn't been sent to a client link yet. Use “Send to client” above and every open, question and acceptance will show up here.`}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "var(--text3)", flex: 1 }}>
              First sent {when(activity.firstSentAt || activity.sentAt)}
              {activity.resendCount > 0 && ` · republished ${activity.resendCount}×`}
            </div>
            <button className="btn" style={{ fontSize: 10, padding: "3px 8px" }}
                    disabled={loading} onClick={onRefresh}>
              {loading ? "…" : "↻"}
            </button>
          </div>

          {/* The internal decision, above the client's — it happens first in the
              workflow, and it is the one Craig may still need to act on. */}
          {published && activity.approval?.state && (
            <div style={{
              background: activity.approval.state === "approved" ? "#ede9fe" : "#ffe4e6",
              border: `1px solid ${activity.approval.state === "approved" ? "#c4b5fd" : "#fda4af"}`,
              borderRadius: 6, padding: "9px 11px", marginBottom: 11,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700,
                            color: activity.approval.state === "approved" ? "#6d28d9" : "#be123c" }}>
                {activity.approval.state === "approved" ? "Authorised" : "Changes needed"}
                {" "}{when(activity.approval.decidedAt)}
                {activity.approval.decidedByName ? ` by ${activity.approval.decidedByName}` : ""}
              </div>
              {activity.approval.text && (
                <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.6,
                              color: activity.approval.state === "approved" ? "#5b21b6" : "#9f1239" }}>
                  “{activity.approval.text}”
                </div>
              )}
              <div style={{ fontSize: 10, marginTop: 6, opacity: 0.85,
                            color: activity.approval.state === "approved" ? "#5b21b6" : "#9f1239" }}>
                Advisory only — sending to the client is never blocked by this.
              </div>
            </div>
          )}

          {acceptance?.accepted && (
            <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6,
                          padding: "9px 11px", marginBottom: 11 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d" }}>
                Accepted {when(acceptance.acceptedAt)}
                {(acceptance.acceptedByName || acceptance.acceptedByEmail) &&
                  ` by ${acceptance.acceptedByName || acceptance.acceptedByEmail}`}
              </div>
              {acceptance.text && (
                <div style={{ fontSize: 11, color: "#166534", marginTop: 4, lineHeight: 1.6 }}>
                  “{acceptance.text}”
                </div>
              )}
              {/* Stated outright, because it is the one rule a user would
                  otherwise have to infer — same treatment the Opportunities
                  detail gives its no-auto-close rule. */}
              <div style={{ fontSize: 10, color: "#166534", marginTop: 6, opacity: 0.85 }}>
                A soft signal only. DocuSign is still the binding signature step,
                and the linked opportunity stays open until you close it.
              </div>
            </div>
          )}

          {/* WHO opened it, not just how many times. Each named recipient has
              their own signed link, so their opens can be listed against them.
              Opens through the copied link are grouped separately rather than
              guessed at — attributing a forward to whoever it was forwarded
              from would be worse than saying "we don't know". */}
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 5 }}>
            Recipients ({sends.length})
          </div>
          {sends.length === 0 ? (
            <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 11 }}>
              No named recipients — this was published without being emailed.
            </div>
          ) : (
            <div style={{ marginBottom: 11 }}>
              {sends.map((s) => {
                const mine = s.id ? views.filter((v) => v.recipientId === s.id) : [];
                const last = mine[mine.length - 1];
                return (
                  <div key={s.id || s.recipientEmail}
                       style={{ padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <div style={{ fontSize: 11, color: "var(--text)", fontWeight: 600, flex: 1 }}>
                        {s.kind === "internal_approval" && (
                          <span title="Internal review copy — not sent to the client"
                                style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                                         background: "#ede9fe", color: "#6d28d9", marginRight: 6,
                                         textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Approver
                          </span>
                        )}
                        {s.recipientName || s.recipientEmail}
                        {s.recipientName && (
                          <span style={{ fontWeight: 400, color: "var(--text3)" }}> · {s.recipientEmail}</span>
                        )}
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 20,
                                     background: mine.length ? "#dcfce7" : "#eef2f7",
                                     color: mine.length ? "#15803d" : "#475569",
                                     textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {mine.length ? (mine.length === 1 ? "Opened" : `Opened ×${mine.length}`) : "Not opened"}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                      {s.legacy
                        ? "sent before per-person links — opens can't be attributed"
                        : `sent ${when(s.sentAt)}${s.sendCount > 1 ? ` · ${s.sendCount} sends` : ""}`}
                      {last && ` · last opened ${when(last.openedAt)}`}
                    </div>
                    {s.publicUrl && !s.legacy && (
                      <button className="btn" style={{ fontSize: 9, padding: "2px 7px", marginTop: 4 }}
                              title={s.publicUrl}
                              onClick={() => { navigator.clipboard?.writeText(s.publicUrl); }}>
                        Copy their link
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {unattributed.length > 0 && (
            <div style={{ marginBottom: 11, padding: "7px 9px", background: "var(--s2)",
                          borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)" }}>
                {unattributed.length === 1 ? "1 open" : `${unattributed.length} opens`} via the shared link
              </div>
              <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2, lineHeight: 1.6 }}>
                Came through “Copy link” or a forward, so there is no name on them.
                Last {when(unattributed[unattributed.length - 1].openedAt)}.
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 5 }}>
            Questions ({qa.length})
          </div>
          {qa.length === 0 ? (
            <div style={{ fontSize: 11, color: "var(--text3)" }}>None asked.</div>
          ) : (
            qa.map((q) => (
              <div key={q.id} style={{ padding: "7px 0", borderTop: "1px solid var(--border)" }}>
                <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}>{q.question}</div>
                <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                  {when(q.askedAt)}
                  {q.askerName ? ` · ${q.askerName}` : ""}
                  {q.askerEmail ? ` · ${q.askerEmail}` : ""}
                  {!q.askerEmail && " · no reply address — the answer only lands on their page"}
                </div>
                {q.answer ? (
                  <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 5, paddingLeft: 9,
                                borderLeft: "2px solid var(--ops)", lineHeight: 1.6 }}>
                    {q.answer}
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                      answered {when(q.answeredAt)}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#b45309", marginTop: 4 }}>
                    Awaiting your answer — check your email for the answer link
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}
    </Panel>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

/* Send-to-client, to one or more NAMED recipients.

   Each person gets their own signed link, so an open can be attributed to them
   rather than to "someone at the client". The "Copy link" button on the toolbar
   still hands out a generic, unattributed link — a forwarded link cannot
   honestly be tied to anyone, and prompting a customer to identify themselves
   would be friction on the one page that has to feel effortless.

   ALWAYS PROMPTS, never silent-sends to a stored address. That rule came out of
   the Kalgoorlie->Cottesloe meeting-form mis-send, and a proposal reaching the
   wrong company is a worse outcome than a form doing so. Addresses are
   prefilled from the document's own contact details, but every one is shown and
   editable before anything leaves.

   Re-sending to someone already on the list reuses their existing link, so the
   one already in their inbox keeps working. Adding a new person leaves
   everyone else's untouched. */
function SendLinkModal({ proposal, kind = "client", activity, busy, senderName, onCancel, onSend }) {
  const approval = kind === "internal_approval";
  const [rows, setRows] = useState([{ name: "", email: "" }]);
  const [body, setBody] = useState("");

  const lead = proposal ? isLead(proposal) : false;
  const noun = lead ? "overview" : "proposal";
  const customer = proposal
    ? (proposal.companyName || proposal.content?.customerName || "your organisation")
    : "";

  /* Re-seed when the modal opens on a different document. Keyed on id, so
     reopening the same one keeps whatever was typed and abandoned. Anyone
     already sent to is prefilled, because the common case for a resend is
     "same people, corrected document". */
  useEffect(() => {
    if (!proposal) return;
    /* Only ever prefill from sends of the SAME kind. Seeding the approval form
       with the client's address — or the client form with accounts@ — is one
       careless Enter away from sending the wrong document to the wrong
       audience. */
    const already = ((activity?.published ? activity.sends : []) || [])
      .filter((s) => (s.kind === "internal_approval") === approval);
    setRows(already.length
      ? already.map((s) => ({ name: s.recipientName || "", email: s.recipientEmail || "" }))
      : approval
        // Editable per send, exactly like the client recipient — this is only
        // the default.
        ? [{ name: "Accounts", email: "accounts@datatech.net.au" }]
        : [{
            name: proposal.content?.customerContact || proposal.content?.customerFullName || "",
            email: proposal.content?.customerEmail || "",
          }]);
    setBody(approval
      ? `Hi,\n\n` +
        `Please review this ${noun} for ${proposal.content?.customerName || "the client"} ` +
        `before it goes out.\n\n` +
        `You can read the full document, ask a question, authorise it, or send it back ` +
        `with the changes you want.\n\n` +
        `${senderName || "Craig"}`
      : `Hi ${proposal.content?.customerContact || "there"},\n\n` +
        `Thanks for your time. Please find your ${noun} below — you can read it online, ` +
        `download a PDF copy, and ask us anything directly on the page.\n\n` +
        `Any questions at all, just ask.\n\n` +
        `${senderName || "Craig"}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.id, approval]);

  if (!proposal) return null;

  const typed = rows.filter((r) => r.email.trim());
  const valid = typed
    .map((r) => ({ name: r.name.trim(), email: r.email.trim() }))
    .filter((r) => /\S+@\S+\.\S+/.test(r.email));
  const someInvalid = valid.length !== typed.length;

  /* Duplicate addresses collapse into one recipient id and therefore one send.
     Harmless, but the modal should say so rather than let Craig believe he
     addressed two people. */
  const dupes = valid
    .map((r) => r.email.toLowerCase())
    .filter((e, i, a) => a.indexOf(e) !== i);

  const setRow = (i, patch) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const known = (activity?.published ? activity.sends : []) || [];
  const alreadySent = (email) =>
    known.some((s) => (s.recipientEmail || "").toLowerCase() === email.trim().toLowerCase());

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={onCancel}>
      <div style={{ background: "var(--s1)", borderRadius: 10, padding: 22, width: 560,
                    maxWidth: "100%", maxHeight: "90vh", overflowY: "auto",
                    border: "1px solid var(--border)" }}
           onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15,
                      color: "var(--text)", marginBottom: 4 }}>
          {approval ? `Send this ${noun} for internal approval` : `Send this ${noun} to ${customer}`}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 14, lineHeight: 1.6 }}>
          {approval
            ? `An internal review copy of ${customer}'s ${noun}. The reviewer sees the same document with Authorise and Not Approved instead of Accept — nothing goes to the client, and approval never blocks sending.`
            : "Each person gets their own link, so you can see who opened it and when. Re-sending to someone already listed reuses their existing link."}
        </div>

        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>
          {approval ? "Approver" : "Recipients"}
        </label>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 6 }}>
            <input className="inp" value={r.name} placeholder="First name"
                   onChange={(e) => setRow(i, { name: e.target.value })}
                   style={{ fontSize: 12, width: 150, marginBottom: 0 }} />
            <div style={{ flex: 1 }}>
              <input className="inp" type="email" value={r.email} placeholder="name@company.com.au"
                     onChange={(e) => setRow(i, { email: e.target.value })}
                     style={{ fontSize: 12, marginBottom: 0 }} />
              {r.email.trim() && alreadySent(r.email) && (
                <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                  already sent — will reuse their link
                </div>
              )}
            </div>
            <button className="btn" style={{ fontSize: 11, padding: "6px 9px" }}
                    title="Remove this recipient"
                    disabled={rows.length === 1}
                    onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>x</button>
          </div>
        ))}
        <button className="btn" style={{ fontSize: 11, marginBottom: 12 }}
                onClick={() => setRows((prev) => [...prev, { name: "", email: "" }])}>
          + Add another recipient
        </button>

        {someInvalid && (
          <div style={{ fontSize: 11, color: "var(--danger)", marginBottom: 10 }}>
            One of those addresses doesn&rsquo;t look valid.
          </div>
        )}
        {dupes.length > 0 && (
          <div style={{ fontSize: 11, color: "#b45309", marginBottom: 10 }}>
            {dupes.join(", ")} is listed twice &mdash; they will get one link, not two.
          </div>
        )}

        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>Your message</label>
        <textarea className="inp" rows={8} value={body}
                  onChange={(e) => setBody(e.target.value)}
                  style={{ fontSize: 12, lineHeight: 1.6, resize: "vertical" }} />
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: -6, marginBottom: 12 }}>
          Everyone gets the same message. Blank lines become paragraphs; the branded
          header, the button and the footer are added around it.
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" style={{ fontSize: 11 }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-strat" style={{ fontSize: 11 }}
                  disabled={busy || !valid.length || someInvalid}
                  title={valid.length ? "" : "Enter at least one valid email address"}
                  onClick={() => onSend(proposal.id, { recipients: valid, emailBodyText: body, kind })}>
            {busy ? "Sending..." : `Send to ${valid.length || "..."}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Delete confirmation. A plain untouched draft gets a light prompt; anything
   that has been sent or carries version history gets an explicit warning that
   names what is being destroyed, because snapshots are the only record of what
   a customer was actually shown. */

/* Shown only when more than one tier is active. With Bronze alone, createProposal
   goes straight there and this never renders — it activates by itself the moment
   Silver or Gold flip to "active". */
function TemplatePicker({ templates, onCancel, onPick }) {
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 12,
                    padding: 24, width: 420, maxWidth: "92vw", boxShadow: "var(--shadow-xl)" }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", margin: "0 0 4px",
                     fontFamily: "var(--ff-head)" }}>Start from which template?</h3>
        <p style={{ fontSize: 11, color: "var(--text3)", margin: "0 0 14px" }}>
          The proposal is seeded with that tier's defaults — you can change anything afterwards.
        </p>
        {templates.map((t) => (
          <button key={t.id} className="btn" style={{ width: "100%", fontSize: 12, marginBottom: 8,
                                                      textAlign: "left", padding: "9px 12px" }}
                  onClick={() => onPick(t.key)}>
            <strong>{t.name}</strong>
            <span style={{ color: "var(--text3)", fontWeight: 400 }}> — {t.tagline}</span>
          </button>
        ))}
        <button className="btn" style={{ width: "100%", fontSize: 11, marginTop: 4 }}
                onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function DeleteConfirm({ proposal, onCancel, onConfirm }) {
  if (!proposal) return null;
  const snaps = proposal.snapshots?.length || 0;
  const heavy = snaps > 0 || proposal.status !== "draft";
  const noun = isLead(proposal) ? "lead" : "proposal";
  const name = proposal.companyName || proposal.content?.customerName || `this ${noun}`;
  const statusLabel = (STATUS_STYLE[proposal.status] || STATUS_STYLE.draft).label.toLowerCase();

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 12,
                    padding: 26, width: 420, maxWidth: "92vw", boxShadow: "var(--shadow-xl)" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#fee2e2",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto 16px", fontSize: 20 }}>🗑</div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", margin: "0 0 10px",
                     textAlign: "center", fontFamily: "var(--ff-head)" }}>
          Delete {noun}?
        </h3>

        {heavy ? (
          <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, margin: "0 0 8px",
                      textAlign: "center" }}>
            <strong style={{ color: "var(--text)" }}>{name}</strong> is marked{" "}
            <strong style={{ color: "var(--text)" }}>{statusLabel}</strong>
            {snaps > 0 && <> and has <strong style={{ color: "var(--text)" }}>{snaps} snapshot{snaps !== 1 ? "s" : ""}</strong></>}
            . Deleting removes all history permanently — including the frozen
            version{snaps !== 1 ? "s" : ""} of what the customer was sent.
          </p>
        ) : (
          <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, margin: "0 0 8px",
                      textAlign: "center" }}>
            <strong style={{ color: "var(--text)" }}>{name}</strong> is an unsent draft with no
            version history. This cannot be undone.
          </p>
        )}

        <p style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.5, margin: "0 0 20px",
                    textAlign: "center" }}>
          To keep it but hide it from the list, use <strong>Archive</strong> instead — that stays
          recoverable.
        </p>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn" style={{ flex: 1, fontSize: 12 }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" style={{ flex: 1, fontSize: 12 }}
                  onClick={() => onConfirm(proposal.id)}>
            {heavy ? "Delete permanently" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  const colors = {
    ok:   { bg: "#dcfce7", fg: "#15803d", bd: "#86efac" },
    err:  { bg: "#fee2e2", fg: "#b91c1c", bd: "#fca5a5" },
    info: { bg: "#e0f2fe", fg: "#0369a1", bd: "#7dd3fc" },
  }[toast.kind] || { bg: "#eef2f7", fg: "#475569", bd: "#cbd5e1" };
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 300, padding: "12px 18px",
                  background: colors.bg, color: colors.fg, border: `1px solid ${colors.bd}`,
                  borderRadius: 10, fontSize: 12, fontWeight: 600, boxShadow: "var(--shadow-xl)" }}>
      {toast.msg}
    </div>
  );
}

function ProposalCard({ p, contractInstances = [], onOpen, onDuplicate, onArchive, onDelete }) {
  const st = STATUS_STYLE[p.status] || STATUS_STYLE.draft;
  const lead = isLead(p);
  /* A Lead has no recommendedPlan — it compares all four — so it must not fall
     through to the "Bronze Sentinel" default, which would label every Lead card
     as a Bronze proposal. */
  const tier = lead ? null : (p.content?.recommendedPlan || "Bronze Sentinel");
  const accent = lead ? LEAD_ACCENT : (TIER_COLOR[tier] || "#0b80a5");
  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 10,
                  overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ height: 3, background: accent }} />
      <div style={{ padding: 14, flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      marginBottom: 10, gap: 8 }}>
          {lead ? (
            <span title="Pre-proposal — compares all four plans"
                  style={{ fontSize: 9, fontWeight: 700, color: "#fff", background: LEAD_ACCENT,
                           padding: "2px 8px", borderRadius: 4, letterSpacing: "0.1em",
                           textTransform: "uppercase", whiteSpace: "nowrap" }}>Lead</span>
          ) : (
            <span style={{ fontSize: 9, fontWeight: 700, color: accent,
                           letterSpacing: "0.08em", textTransform: "uppercase" }}>{tier}</span>
          )}
          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                         background: st.bg, color: st.fg, textTransform: "uppercase",
                         letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{st.label}</span>
        </div>
        {/* A number you can search for but never see is a number nobody uses.
            Leads carry none by design, so this simply does not render there. */}
        {p.proposalNumber != null && (
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                        color: "var(--text3)", fontVariantNumeric: "tabular-nums",
                        marginBottom: 2 }}>
            {formatProposalNumber(p.proposalNumber)}
          </div>
        )}
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 2,
                      letterSpacing: "-0.2px" }}>
          {p.companyName || p.content?.customerName || "Untitled"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10 }}>
          {p.content?.customerFullName || "—"}
        </div>
        <div style={{ display: "flex", gap: 14, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
          <Meta label="Updated" value={fmtDate(p.updatedAt)} />
          <Meta label="Versions" value={String(p.snapshots?.length || 0)} />
        </div>
        {/* Only what the state blob already holds. Open counts live in a
            per-proposal blob, so showing them here would be one request per
            card — they appear on the opened proposal instead. */}
        {(() => {
          const badges = dashboardBadgesOf(p, contractInstances);
          if (!badges.length) return null;
          return (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
              {badges.map((b) => (
                <span key={b.key}
                      style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                               background: b.bg, color: b.fg, textTransform: "uppercase",
                               letterSpacing: "0.06em" }}>{b.label}</span>
              ))}
            </div>
          );
        })()}
      </div>
      <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)", display: "flex", gap: 6 }}>
        <button className="btn btn-strat" style={{ flex: 1, fontSize: 10 }} onClick={onOpen}>Open</button>
        <button className="btn" style={{ fontSize: 10 }} onClick={onDuplicate}>Copy</button>
        <button className="btn" style={{ fontSize: 10 }}
                title={p.status === "archived" ? "Restore to draft" : "Hide from the list — recoverable"}
                onClick={onArchive}>
          {p.status === "archived" ? "Restore" : "Archive"}
        </button>
        <button className="btn btn-danger" style={{ fontSize: 10, padding: "4px 8px" }}
                title="Delete permanently — removes all version history"
                onClick={onDelete}>🗑</button>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                    color: "var(--text3)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 11, color: "var(--text2)" }}>{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Settings panel — the MSP OS equivalent of the standalone template's
   injected #__dtp_panel. Everything the document can't sensibly host inline.
   ══════════════════════════════════════════════════════════════════════ */